import { Suspense, lazy, useEffect, useMemo } from 'react';
import { CssBaseline, ThemeProvider, useMediaQuery } from '@mui/material';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { createSmartHomeTheme } from '@smart-home/design-tokens';
import { ShellLayout, defaultNavItems } from '@smart-home/smarthome-ui';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { getCurrentUser } from './api/user';
import { LanguageSelector } from './components/LanguageSelector';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { RouteFallback } from './components/RouteFallback';
import { RealtimeProvider, useRealtime } from './hooks/useRealtime';
import { useI18n } from './i18n/I18nProvider';
import { useAuthStore } from './store/authStore';

const AuthPage = lazy(() => import('./pages/AuthPage').then((module) => ({ default: module.AuthPage })));
const DashboardPage = lazy(() => import('./pages/DashboardPage').then((module) => ({ default: module.DashboardPage })));
const RoomPage = lazy(() => import('./pages/RoomPage').then((module) => ({ default: module.RoomPage })));
const DeviceDetailPage = lazy(() => import('./pages/DeviceDetailPage').then((module) => ({ default: module.DeviceDetailPage })));
const ConnectHomePage = lazy(() => import('./pages/ConnectHomePage').then((module) => ({ default: module.ConnectHomePage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then((module) => ({ default: module.SettingsPage })));
const HomesPage = lazy(() => import('./pages/HomesPage').then((module) => ({ default: module.HomesPage })));
const HomeDetailPage = lazy(() => import('./pages/HomeDetailPage').then((module) => ({ default: module.HomeDetailPage })));
const ScenesPage = lazy(() => import('./pages/ScenesPage').then((module) => ({ default: module.ScenesPage })));
const AdminDevicesPage = lazy(() => import('./pages/admin/AdminDevicesPage').then((module) => ({ default: module.AdminDevicesPage })));
const DeviceManagementPage = lazy(() => import('./pages/admin/DeviceManagementPage').then((module) => ({ default: module.DeviceManagementPage })));

function getCurrentNav(pathname: string) {
  if (pathname.startsWith('/homes')) return 'homes';
  if (pathname.startsWith('/rooms')) return 'rooms';
  if (pathname.startsWith('/connect')) return 'connect';
  if (pathname.startsWith('/settings')) return 'settings';
  if (pathname.startsWith('/admin/device-management')) return 'device-management';
  if (pathname.startsWith('/admin/devices')) return 'admin-devices';
  return 'dashboard';
}

function AppShellInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const updateUser = useAuthStore((state) => state.updateUser);
  const { t } = useI18n();
  const { status: realtimeStatus } = useRealtime();

  useEffect(() => {
    if (!user) return;
    getCurrentUser().then((profile) => updateUser(profile as any)).catch(() => undefined);
  }, [user?.id, updateUser]);

  const navItems = user?.role === 'admin'
    ? [
      ...defaultNavItems,
      { label: t('adminDevices'), value: 'admin-devices', icon: <AdminPanelSettingsRoundedIcon />, href: '/admin/devices' },
      { label: 'Device management', value: 'device-management', icon: <AdminPanelSettingsRoundedIcon />, href: '/admin/device-management' }
    ]
    : defaultNavItems;

  return (
    <ShellLayout
      currentNav={getCurrentNav(location.pathname)}
      navItems={navItems}
      onNavigate={navigate}
      topbar={
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" sx={{ mb: 3 }}>
          <Stack>
            <Typography variant="overline" color="primary.main">{t('responsiveWorkspace')}</Typography>
            <Typography variant="h5">{t('controlCenter')}</Typography>
          </Stack>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Chip
              size="small"
              color={realtimeStatus === 'connected' ? 'success' : realtimeStatus === 'connecting' ? 'warning' : 'default'}
              variant={realtimeStatus === 'connected' ? 'filled' : 'outlined'}
              label={realtimeStatus === 'connected' ? 'Realtime connected' : realtimeStatus === 'connecting' ? 'Realtime connecting' : 'Realtime disconnected'}
            />
            <LanguageSelector />
            <NotificationsRoundedIcon color="disabled" />
            <Stack spacing={0.2}>
              <Typography variant="body2">{user?.email}</Typography>
              <Typography variant="caption" color="text.secondary">{user?.role}</Typography>
            </Stack>
          </Stack>
        </Stack>
      }
      sidebarFooter={
        <Stack spacing={1.5} sx={{ p: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <HomeRoundedIcon color="primary" />
            <Stack>
              <Typography variant="body2">{t('ecosystemSynced')}</Typography>
              <Typography variant="caption" color="text.secondary">{t('authRealtimeReady')}</Typography>
            </Stack>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ cursor: 'pointer' }} onClick={logout}>
            {t('logout')}
          </Typography>
        </Stack>
      }
    >
      <Suspense fallback={<RouteFallback label="Loading smart-home workspace..." />}>
        <Routes>
          <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/homes" element={<ProtectedRoute><HomesPage /></ProtectedRoute>} />
          <Route path="/homes/:homeId" element={<ProtectedRoute><HomeDetailPage /></ProtectedRoute>} />
          <Route path="/scenes" element={<ProtectedRoute><ScenesPage /></ProtectedRoute>} />
          <Route path="/rooms/:roomId" element={<ProtectedRoute><RoomPage /></ProtectedRoute>} />
          <Route path="/devices/:entityId" element={<ProtectedRoute><DeviceDetailPage /></ProtectedRoute>} />
          <Route path="/connect" element={<ProtectedRoute><ConnectHomePage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/admin/devices" element={<ProtectedRoute><AdminDevicesPage /></ProtectedRoute>} />
          <Route path="/admin/device-management" element={<ProtectedRoute><DeviceManagementPage /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ShellLayout>
  );
}

function AppShell() {
  return (
    <RealtimeProvider>
      <AppShellInner />
    </RealtimeProvider>
  );
}

export default function App() {
  const storedThemePreference = useAuthStore((state) => state.user?.preferences?.theme);
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const themeMode = storedThemePreference ?? (prefersDark ? 'dark' : 'light');
  const theme = useMemo(() => createSmartHomeTheme(themeMode), [themeMode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/*" element={<AppShell />} />
        </Routes>
      </Suspense>
    </ThemeProvider>
  );
}
